import { Box, Grid } from '@mui/material'
import { Form, Formik, FormikValues } from 'formik'
import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import {
  useCreateUserGroupMutation,
  useUpdateUserGroupMutation,
} from '../../../../app/redux-fetch/apiQuery'
import SubmitButtons from '../../../../components/Buttons/SubmitButtons'
import TabWrapper, { TabItem } from '../../../../components/TabWrapper/TabWrapper'
import { useSideModalContext } from '../../../Assets/modals/SideModalContext'
import { useNotificationStack } from '../../../Shipments/hooks/useNotificationStack'
import MainInfo from './MainInfo'
import UserGroupMembers from './UserGroupMembers'
import UserGroupRoles from './UserGroupRoles'
import { getUserGroupValidationSchema } from './UserGroupValidationSchema'
import messages from './messages'
import { IUserGroup } from './types'

type UserGroupFormProps = {
  userGroup: IUserGroup
  isDisabled: boolean
}

const UserGroupForm = ({ userGroup, isDisabled }: UserGroupFormProps) => {
  const isNew = !userGroup.id

  const { formatMessage } = useIntl()
  const { enqueue } = useNotificationStack()

  const [createUserGroup] = useCreateUserGroupMutation()
  const [updateUserGroup] = useUpdateUserGroupMutation()

  const validationSchema = getUserGroupValidationSchema(formatMessage)
  const { onClose } = useSideModalContext()

  const tabs: TabItem[] = useMemo(() => [
    {
      label: formatMessage(messages.tabGeneral),
      content: (
        <Grid item xs={12}>
          <MainInfo isDisabled={isDisabled} />
        </Grid>
      ),
    },
    {
      label: formatMessage(messages.tabUsers),
      content: (
        <Grid item xs={12}>
          <UserGroupMembers isDisabled={isDisabled} />
        </Grid>
      ),
    },
    {
      label: formatMessage(messages.tabRoles),
      content: (
        <Grid item xs={12}>
          <UserGroupRoles isDisabled={isDisabled} />
        </Grid>
      ),
    },
  ], [formatMessage, isDisabled])

  return (
    <Formik
      initialValues={userGroup}
      onSubmit={async (values: FormikValues) => {
        const userAllocations = values.users?.map((user: any) => {
          return {
            userId: user.id,
            userGroupId: values.id,
          }
        })
        const userGroupRoles = values.roles?.map((role: any) => {
          return {
            roleId: role.id,
            userGroupId: values.id,
          }
        })
        if (!values.id) {
          const result = await createUserGroup({
            tenantId: values.tenantId ?? -1,
            customerId: values.customerId,
            body: { ...values, userAllocations, roleAllocations: userGroupRoles } as IUserGroup,
          })
          if ('error' in result) return
          enqueue(formatMessage(messages.userGroupCreated))
        } else {
          const result = await updateUserGroup({
            tenantId: values.tenantId ?? -1,
            customerId: values.customerId,
            body: { ...values, userAllocations, roleAllocations: userGroupRoles } as IUserGroup,
          })
          if ('error' in result) return
          enqueue(formatMessage(messages.userGroupUpdated))
        }
        onClose()
      }}
      validationSchema={validationSchema}
    >
      <Form>
        <Box sx={{ width: '100%', p: '40px' }}>
          <Box sx={{ width: '100%', p: '40px' }}>
            <TabWrapper tabs={tabs} variant='standard' />
          </Box>

          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              width: '100%',
              padding: '16px',
              backgroundColor: '#fff',
              borderTop: '1px solid #e0e0e0',
            }}
          >
            {!isDisabled && <SubmitButtons create={isNew} onCancel={onClose} />}
          </Box>
        </Box>
      </Form>
    </Formik>
  )
}

export default UserGroupForm
